/**
 * redirects.ts — API client for the 301 Redirection Management feature.
 *
 * GET /wp-json/otpl/v1/redirects/active
 *
 * Read-only, public, no auth — returns every ACTIVE redirect row managed
 * from WP Admin → Tools → 301 Redirections (`class-redirects.php`). The
 * Frontend fetches this once (cached — see `useRedirects.ts`) and checks
 * every route change against it before rendering, since this is a
 * headless SPA and WordPress's own redirect hooks never run for a normal
 * page URL (see the PHP class's docblock for the full explanation).
 */
import { apiClient } from './client';

export interface RedirectRule {
  /** Normalised site-relative source path, e.g. "/old-page". */
  source: string;
  /** Either a site-relative path ("/new-page") or an absolute https:// URL. */
  destination: string;
  /** 301 (permanent) or 302 (temporary) — mirrors HTTP semantics for the
   *  benefit of any future server-level redirect layer; the client-side
   *  navigation itself behaves identically for both today. */
  type: 301 | 302;
}

export async function fetchActiveRedirects(): Promise<RedirectRule[]> {
  try {
    const res = await apiClient.get<RedirectRule[]>('/wp-json/otpl/v1/redirects/active');
    return Array.isArray(res.data) ? res.data : [];
  } catch {
    // A redirects-fetch failure must never block normal site navigation —
    // fail open (no redirects applied) rather than fail closed.
    return [];
  }
}
